// src/utils/previewPdfExport.js
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// Yield to the browser so progress UI can repaint between heavy steps
const nextFrame = () => new Promise((resolve) => setTimeout(resolve, 30));

const KEEP_TAGS = new Set(['HEAD', 'STYLE', 'LINK', 'META', 'TITLE', 'BASE']);

// 210mm at 96dpi — the sheet is laid out at this width for capture regardless of the on-screen
// modal width, so a narrow viewport no longer produces an enlarged, wrapped, multi-page PDF.
const A4_PX = 794;
// A capture only marginally taller than one page is scaled to fit instead of spilling onto a near-empty page.
const SINGLE_PAGE_TOLERANCE = 1.03;

// html2canvas clones the whole document before rendering; skip everything that isn't the
// target, its ancestors/descendants, or stylesheets — this is what made the capture lag.
const buildIgnoreFilter = (element) => (node) => {
  if (node.nodeType !== 1 || KEEP_TAGS.has(node.tagName)) return false;
  if (node.closest?.('head')) return false;
  return !(node === element || node.contains(element) || element.contains(node));
};

/**
 * Capture a rendered DOM element (e.g. the A4 print preview) into an A4 PDF,
 * so the downloaded file is identical to what's on screen. Tall content spans pages.
 * onProgress(percent) is called between steps.
 */
export const exportElementToA4Pdf = async (element, fileName = 'document.pdf', onProgress) => {
  if (!element) return;
  const report = async (pct) => {
    onProgress?.(pct);
    await nextFrame();
  };

  await report(10);
  // Lay the live sheet out at A4 width for the capture (html2canvas sizes from the live element),
  // then restore it; the progress popup covers the brief reflow.
  const { width, maxWidth, margin } = element.style;
  Object.assign(element.style, { width: `${A4_PX}px`, maxWidth: `${A4_PX}px`, margin: '0' });
  let canvas;
  try {
    canvas = await html2canvas(element, {
      scale: 1.75,
      useCORS: true,
      backgroundColor: '#ffffff',
      windowWidth: A4_PX,
      logging: false,
      ignoreElements: buildIgnoreFilter(element),
    });
  } finally {
    Object.assign(element.style, { width, maxWidth, margin });
  }

  await report(65);
  const imgData = canvas.toDataURL('image/jpeg', 0.92);

  await report(80);
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const imgH = (canvas.height * pageW) / canvas.width;

  if (imgH <= pageH * SINGLE_PAGE_TOLERANCE) {
    const fitH = Math.min(imgH, pageH);
    const fitW = (pageW * fitH) / imgH;
    doc.addImage(imgData, 'JPEG', (pageW - fitW) / 2, 0, fitW, fitH);
  } else {
    let offset = 0;
    doc.addImage(imgData, 'JPEG', 0, 0, pageW, imgH);
    while (imgH - offset > pageH + 0.5) {
      offset += pageH;
      doc.addPage();
      doc.addImage(imgData, 'JPEG', 0, -offset, pageW, imgH);
    }
  }

  await report(95);
  doc.save(fileName);
  await report(100);
};
